// Scoring and round progression. Pure logic, unit-tested in Node.
import { GOAL, SHOTS_PER_ROUND, GOALS_TO_ADVANCE, PERFECT_ZONE } from './config.js';

export const BASE_GOAL_POINTS = 100;

/** Round multiplier: round 1 x1, round 2 x1.5, round 3 x2 ... */
export function roundMultiplier(round) {
  return 1 + 0.5 * Math.max(0, round);
}

export function isPerfectPower(power) {
  return power >= PERFECT_ZONE[0] && power <= PERFECT_ZONE[1];
}

/**
 * Points for one shot. Only goals score.
 * shot = { outcome, crossX, crossY, speed, curve, power, hitFrame, streak, round }
 * Returns { points, tags: [{ label, points }] } where tags explain the total (before the multiplier).
 */
export function scoreShot(shot) {
  if (shot.outcome !== 'goal') return { points: 0, tags: [] };
  const tags = [{ label: 'Goal', points: BASE_GOAL_POINTS }];
  const ax = Math.abs(shot.crossX);
  const wide = ax >= GOAL.halfWidth - 1.06; // outer ~1 m channel next to each post
  if (wide && shot.crossY >= 1.6) tags.push({ label: 'Top corner', points: 150 });
  else if (wide && shot.crossY <= 0.6) tags.push({ label: 'Bottom corner', points: 75 });
  else if (wide) tags.push({ label: 'Placed', points: 50 });
  if (shot.speed >= 27) tags.push({ label: 'Rocket', points: 50 });
  if (Math.abs(shot.curve) > 0.01) tags.push({ label: 'Curler', points: 25 });
  if (isPerfectPower(shot.power)) tags.push({ label: 'Perfect strike', points: 25 });
  if (shot.hitFrame) tags.push({ label: 'Off the woodwork', points: 50 });
  if (shot.streak >= 3) tags.push({ label: `Streak x${shot.streak}`, points: 50 });
  const raw = tags.reduce((s, t) => s + t.points, 0);
  return { points: Math.round(raw * roundMultiplier(shot.round)), tags };
}

/** Bonus awarded when a round is cleared. */
export function roundBonus(round, goalsInRound) {
  let bonus = 200 * (round + 1);
  if (goalsInRound === SHOTS_PER_ROUND) bonus += 300;
  return bonus;
}

/**
 * Status of the current round after `shots` attempts with `goals` scored.
 * 'continue' | 'cleared' (all shots taken and enough goals) | 'eliminated' (cannot reach the target any more)
 */
export function roundStatus(shots, goals) {
  const remaining = SHOTS_PER_ROUND - shots;
  if (goals + remaining < GOALS_TO_ADVANCE) return 'eliminated';
  if (remaining === 0) return 'cleared';
  return 'continue';
}

/** Integer accuracy percentage used for the leaderboard. */
export function accuracy(goals, shots) {
  return shots > 0 ? Math.round((100 * goals) / shots) : 0;
}

/** Leaderboard stats for a finished run, exactly matching startng.json keys. */
export function runStats(run) {
  const clamp = (n) => Math.max(0, Math.min(1_000_000_000, Math.round(n) || 0));
  return {
    points: clamp(run.points),
    goals: clamp(run.goals),
    rounds: clamp(run.roundsCleared),
    accuracy: clamp(accuracy(run.goals, run.shots)),
  };
}
